import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useProfilePayloadList } from "@/hooks/usePayloads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, ArrowRight, Check, Loader2, Shield, Wifi, Monitor, Settings, Smartphone, CircleDot } from "lucide-react";
import { WizardStepIndicator, WizardStepDef } from "@/components/ui/wizard-step-indicator";
import { PayloadSelector } from "./PayloadSelector";
import { PayloadConfigPanel } from "./PayloadConfigPanel";
import { validateHomeScreenConfig, summarize as summarizeHomeScreen } from "./homescreen/validation";
import { formatValidationErrors } from "./formatValidationError";
import { CustomerSelectionStep } from "@/components/shared/CustomerSelectionStep";
import { ScopeAndAssignStep } from "@/components/shared/ScopeAndAssignStep";
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import * as payloadsApi from "@/services/payloadsApi";
import * as policiesApi from "@/services/policiesApi";
import * as tagAssignmentApi from "@/services/tagAssignmentApi";
import { getCurrentUserEmail } from "@/lib/auth";
import type { ProfilePayloadSummary, CreatePayloadInput, PolicyDetails, PolicyPayload, MultiplePolicyTag } from "@/types/api";
import { cn } from "@/lib/utils";
import { getCustomerName } from "@/utils/customerLookup";
import { stripEditorOnlyFields } from "@/utils/stripEditorOnlyFields";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { UnsavedChangesDialog } from "@/components/ui/unsaved-changes-dialog";

interface ConfigurationWizardProps {
  editMode?: boolean;
  editId?: string;
  initialData?: PolicyDetails;
}

interface ConfigTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category: string;
  platforms: string[];
  /**
   * Keyword hints used to auto-select payloads from the catalog.
   * MUST only reference profile-kind payload identifiers/titles.
   * Command-kind entries (e.g. DeviceLock, EraseDevice) are explicitly
   * filtered out at match time via `profilePayloads` and must not be
   * added here, even if a backend command happens to share a keyword.
   */
  payloads: string[];
}

const configTemplates: ConfigTemplate[] = [
  {
    id: "security-baseline",
    name: "Security Baseline",
    description: "Essential security settings for all devices including passcode policies and restrictions.",
    icon: <Shield className="h-8 w-8 text-info" />,
    category: "Security",
    platforms: ["macOS", "iOS", "iPadOS"],
    payloads: ["passcode", "restrictions", "firewall"]
  },
  {
    id: "corporate-wifi",
    name: "Corporate Wi-Fi",
    description: "Automatically connect devices to corporate Wi-Fi networks with proper security settings.",
    icon: <Wifi className="h-8 w-8 text-success" />,
    category: "Network",
    platforms: ["macOS", "iOS", "iPadOS", "tvOS"],
    payloads: ["wifi"]
  },
  {
    id: "device-management",
    name: "Device Management",
    description: "Comprehensive device management settings for corporate environments.",
    icon: <Monitor className="h-8 w-8 text-primary" />,
    category: "Management",
    platforms: ["macOS", "iOS", "iPadOS"],
    payloads: ["restrictions", "privacy-preferences"]
  },
  {
    id: "mobile-security",
    name: "Mobile Security",
    description: "Security configuration optimized for mobile devices and remote work.",
    icon: <Smartphone className="h-8 w-8 text-warning" />,
    category: "Security",
    platforms: ["iOS", "iPadOS"],
    payloads: ["passcode", "restrictions", "single-app-mode"]
  },
  {
    id: "custom-profile",
    name: "Custom Profile",
    description: "Start with a blank profile and configure payloads manually.",
    icon: <Settings className="h-8 w-8 text-muted-foreground" />,
    category: "Custom",
    platforms: ["macOS", "iOS", "iPadOS", "tvOS"],
    payloads: []
  }
];

const STEPS_CREATE: WizardStepDef[] = [
  { id: "template", name: "Choose Template", description: "Pick a starting point" },
  { id: "info", name: "Policy Info", description: "Name and description" },
  { id: "payloads", name: "Select Payloads", description: "Choose payload types" },
  { id: "configure", name: "Configure Payloads", description: "Set payload values" },
  { id: "scope", name: "Scope & Assign", description: "Customer targeting and deployment" },
  { id: "review", name: "Review", description: "Confirm & save" },
];

const STEPS_EDIT: WizardStepDef[] = [
  { id: "info", name: "Policy Info", description: "Name and description" },
  { id: "payloads", name: "Select Payloads", description: "Choose payload types" },
  { id: "configure", name: "Configure Payloads", description: "Set payload values" },
  { id: "scope", name: "Scope & Assign", description: "Customer targeting and deployment" },
  { id: "review", name: "Review", description: "Confirm & save" },
];

export function ConfigurationWizard({ editMode = false, editId, initialData }: ConfigurationWizardProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const STEPS = editMode ? STEPS_EDIT : STEPS_CREATE;
  const totalSteps = STEPS.length;

  // Step indices
  const templateStep = editMode ? -1 : 0;
  const infoStep = editMode ? 0 : 1;
  const payloadsStep = editMode ? 1 : 2;
  const configureStep = editMode ? 2 : 3;
  const scopeStep = editMode ? 3 : 4;
  const reviewStep = editMode ? 4 : 5;

  // State
  const [policyName, setPolicyName] = useState("");
  const [policyDescription, setPolicyDescription] = useState("");
  const [selectedPayloads, setSelectedPayloads] = useState<ProfilePayloadSummary[]>([]);
  const [payloadConfigs, setPayloadConfigs] = useState<Record<string, Record<string, unknown>>>({});
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [allCustomers, setAllCustomers] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  // "Any user interaction = dirty" semantics. We flip this on the first
  // mutating handler call (config edits, name/description typing, payload
  // selection, scope changes, tag changes) and clear it after a successful
  // save / draft. The unsaved-changes guard then intercepts Back/Cancel
  // and the browser tab close while the wizard is dirty.
  const [isDirty, setIsDirty] = useState(false);
  // Tracks whether we are mid-navigation away after a successful save so
  // the guard does not re-prompt while the route transition unwinds.
  const skipGuardRef = useRef(false);
  const markDirty = useCallback(() => {
    if (skipGuardRef.current) return;
    setIsDirty(true);
  }, []);
  // Snapshot of the initial wizard state captured the first time we hydrate
  // from props. Used by the discard flow to restore the form to its starting
  // point — for edit mode that's the server-loaded policy; for create mode
  // it's the empty form. Captured once so subsequent edits don't shift the
  // restore target.
  const initialSnapshotRef = useRef<{
    name: string;
    description: string;
    payloads: ProfilePayloadSummary[];
    configs: Record<string, Record<string, unknown>>;
    customers: string[];
    allCustomers: boolean;
    tagIds: string[];
  } | null>(null);

  // Pre-populate from initialData in edit mode
  useEffect(() => {
    if (editMode && initialData) {
      const nextName = initialData.name || "";
      const nextDescription = initialData.description || "";
      setPolicyName(nextName);
      setPolicyDescription(nextDescription);

      let nextPayloads: ProfilePayloadSummary[] = [];
      let nextConfigs: Record<string, Record<string, unknown>> = {};
      if (initialData.payloads && initialData.payloads.length > 0) {
        nextPayloads = initialData.payloads
          .filter((p: PolicyPayload) => (p.kind || "").toLowerCase() !== "command")
          .map((p: PolicyPayload) => ({
            // Saved policies only persist profile-kind payloads; the filter
            // above guards against legacy/command rows leaking through.
            kind: "profile" as const,
            identifier: p.identifier,
            title: p.title,
          }));
        setSelectedPayloads(nextPayloads);

        for (const p of initialData.payloads) {
          if (p.kind && p.identifier) {
            const key = `${p.kind}:${p.identifier}`;
            let parsed: Record<string, unknown> = {};
            if (typeof p.configJson === "string") {
              try { parsed = JSON.parse(p.configJson); } catch {}
            } else if (typeof p.configJson === "object" && p.configJson) {
              parsed = p.configJson as Record<string, unknown>;
            }
            nextConfigs[key] = parsed;
          }
        }
        setPayloadConfigs(nextConfigs);
      }

      // Capture snapshot exactly once so the first hydration becomes the
      // discard restore target. Re-fires of this effect (rare, but possible
      // if props churn) won't move the goalposts on the user.
      if (!initialSnapshotRef.current) {
        initialSnapshotRef.current = {
          name: nextName,
          description: nextDescription,
          payloads: nextPayloads,
          configs: nextConfigs,
          customers: [],
          allCustomers: false,
          tagIds: [],
        };
      }
    } else if (!editMode && !initialSnapshotRef.current) {
      // Create mode: empty form is the restore target.
      initialSnapshotRef.current = {
        name: "",
        description: "",
        payloads: [],
        configs: {},
        customers: [],
        allCustomers: false,
        tagIds: [],
      };
    }
  }, [editMode, initialData]);

  // Profile-only catalog: command-kind entries are filtered at the hook layer
  // so template counts and auto-selection never include MDM commands.
  const { data: profilePayloads } = useProfilePayloadList();

  const templatePayloadCounts = useMemo(() => {
    if (!profilePayloads) return {};
    const counts: Record<string, number> = {};
    for (const t of configTemplates) {
      if (t.id === "custom-profile") { counts[t.id] = 0; continue; }
      counts[t.id] = profilePayloads.filter((p: ProfilePayloadSummary) => {
        const id = (p.identifier || "").toLowerCase();
        const title = (p.title || "").toLowerCase();
        return t.payloads.some((kw) => {
          const k = kw.toLowerCase();
          return id.includes(k) || title.includes(k);
        });
      }).length;
    }
    return counts;
  }, [profilePayloads]);

  const handleTemplateSelect = useCallback((template: ConfigTemplate) => {
    setSelectedTemplateId(template.id);
    if (template.id !== "custom-profile") {
      setPolicyName(template.name);
      setPolicyDescription(template.description);

      if (profilePayloads && template.payloads.length > 0) {
        const matched = profilePayloads.filter((p: ProfilePayloadSummary) => {
          const id = (p.identifier || "").toLowerCase();
          const title = (p.title || "").toLowerCase();
          return template.payloads.some((keyword) => {
            const kw = keyword.toLowerCase();
            return id.includes(kw) || title.includes(kw);
          });
        });
        if (matched.length > 0) {
          setSelectedPayloads(matched);
        }
      }
    } else {
      setPolicyName("");
      setPolicyDescription("");
      setSelectedPayloads([]);
    }
    setStep(infoStep);
  }, [profilePayloads, infoStep]);

  const canProceed = () => {
    if (step === templateStep) return false;
    if (step === infoStep) return policyName.trim().length > 0;
    if (step === payloadsStep) return selectedPayloads.length > 0;
    return true;
  };

  const handleNext = () => {
    if (step < totalSteps - 1) setStep(step + 1);
  };

  // Plain-string handle for the unsaved-changes guard. The hook compares
  // `current` vs the captured baseline via deep-equal — flipping this string
  // when `isDirty` toggles makes the guard treat any user interaction as
  // dirty without us having to deep-snapshot every form field.
  const dirtyToken = isDirty ? "dirty" : "clean";
  const guard = useUnsavedChangesGuard({
    open: true,
    current: dirtyToken,
    baseline: "clean",
    onClose: () => navigate("/configurations"),
    onDiscard: () => {
      // Restore every form field to the captured initial snapshot before
      // navigating away. Without this, the next mount of the wizard (or a
      // peer surface that reads the same atoms) would show the half-edited
      // values until the server round-trip completes.
      const snap = initialSnapshotRef.current;
      if (snap) {
        setPolicyName(snap.name);
        setPolicyDescription(snap.description);
        setSelectedPayloads(snap.payloads);
        setPayloadConfigs(snap.configs);
        setSelectedCustomers(snap.customers);
        setAllCustomers(snap.allCustomers);
        setSelectedTagIds(snap.tagIds);
      }
      setIsDirty(false);
    },
    surface: "ConfigurationWizard",
    isSaving: isSubmitting,
  });

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
      return;
    }
    // Step 0 → leaving the wizard. Route through the guard so dirty edits
    // surface a confirm prompt instead of silently dropping the work.
    guard.requestClose(false);
  };

  const handleConfigChange = (kind: string, identifier: string, config: Record<string, unknown>) => {
    setPayloadConfigs((prev) => ({ ...prev, [`${kind}:${identifier}`]: config }));
    markDirty();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      for (const payload of selectedPayloads) {
        const key = `${payload.kind}:${payload.identifier}`;
        // Defensive sanitization for ALL payloads: strip `_`-prefixed editor
        // bookkeeping and known historical leaks (e.g. WebClip `IconKind`)
        // before sending to /validate or policy create. Apple schemas use
        // `additionalProperties: false`.
        const config = stripEditorOnlyFields(payloadConfigs[key] || {});
        payloadConfigs[key] = config;

        // Client-side gating for the visual home screen layout editor.
        const idLower = (payload.identifier || "").toLowerCase().replace(/\s+/g, "");
        const titleLower = (payload.title || "").toLowerCase().trim();
        const isHomeScreenPayload =
          idLower === "com.apple.homescreenlayout" ||
          idLower.endsWith(".homescreenlayout") ||
          titleLower === "home screen layout";
        if (isHomeScreenPayload) {
          const issues = validateHomeScreenConfig(config);
          const { errors } = summarizeHomeScreen(issues);
          if (errors > 0) {
            toast({
              title: `Fix home screen layout: ${payload.title || payload.identifier}`,
              description: formatValidationErrors(
                issues.filter((i) => i.level === "error").map((i) => ({ message: i.message })),
              ),
              variant: "destructive",
            });
            setIsSubmitting(false);
            return;
          }
        }

        // Server-side validation (live as of May 7, 2026). Returns
        // structured errors at JSON paths plus a `normalizedConfig` with
        // schema defaults expanded. We persist the normalized form so the
        // saved profile is fully specified and immune to future default
        // drift in Apple's schema.
        try {
          const result = await payloadsApi.validatePayload(
            payload.kind!,
            payload.identifier!,
            config,
          );
          if (!result.valid) {
            const errs = result.errors ?? [];
            // Log the raw response so we can diagnose unfamiliar error shapes
            // even when the toast can't render every field.
            console.warn("[validatePayload] rejected", {
              kind: payload.kind,
              identifier: payload.identifier,
              sentConfig: config,
              response: result,
            });
            // One-shot diagnostic: log the backend's authoritative schema for
            // homescreenlayout so we can see exactly which keys Dock/Pages
            // items are allowed to carry.
            if (/homescreenlayout/i.test(payload.identifier ?? "")) {
              try {
                const schema = await payloadsApi.getPayloadSchema(
                  payload.kind!,
                  payload.identifier!,
                );
                console.info("[validatePayload] homescreenlayout schema", schema);
              } catch (schemaErr) {
                console.warn("[validatePayload] failed to fetch schema", schemaErr);
              }
            }
            toast({
              title: `Fix ${payload.title || payload.identifier}`,
              description: formatValidationErrors(errs),
              variant: "destructive",
            });
            setIsSubmitting(false);
            return;
          }
          if (result.normalizedConfig && typeof result.normalizedConfig === "object") {
            payloadConfigs[key] = result.normalizedConfig as Record<string, unknown>;
          }
        } catch (e: any) {
          // Network/auth failure — fall back to sending raw config; the
          // policy create endpoint performs authoritative validation.
          console.warn("[ConfigurationWizard] validate call failed for", payload.identifier, e);
        }
      }

      const payloads: CreatePayloadInput[] = selectedPayloads.map((p) => ({
        kind: p.kind!,
        identifier: p.identifier!,
        config: payloadConfigs[`${p.kind}:${p.identifier}`] || {},
      }));

      const request = {
        name: policyName,
        description: policyDescription || undefined,
        payloads,
        tenantIds: allCustomers ? undefined : selectedCustomers,
        allTenants: allCustomers || undefined,
      };

      let policyId = editId;
      if (editMode && editId) {
        await policiesApi.updatePolicy(editId, request);
        toast({ title: "Configuration updated", description: `"${policyName}" has been updated successfully.` });
      } else {
        const result = await policiesApi.createPolicy(request);
        policyId = result.id;
        toast({ title: "Configuration created", description: `"${policyName}" has been created successfully.` });
      }

      // Assign tags if any selected (this IS the deployment)
      if (policyId && selectedTagIds.length > 0) {
        const userEmail = getCurrentUserEmail() || "system";
        const payload: MultiplePolicyTag = {
          policy_ids: [policyId],
          tag_ids: selectedTagIds,
          created_by: userEmail,
          updated_by: userEmail,
        };
        await tagAssignmentApi.assignPolicyTags(payload);
      }
      // Successful save — clear dirty state and bypass the guard so the
      // route change to the listing doesn't trigger a discard prompt.
      // NOTE: we deliberately do NOT call guard.markClean() here. The
      // guard's baseline is the literal string "clean", and flipping
      // setIsDirty(false) makes dirtyToken === baseline on the next render,
      // which already drops the beforeunload listener. Calling markClean
      // would freeze the now-stale "dirty" token as the new baseline and
      // re-arm the guard, leaving the wizard perma-dirty until unmount.
      skipGuardRef.current = true;
      setIsDirty(false);
      navigate("/configurations");
    } catch (error: any) {
      // Surface the backend error detail so users see WHY save failed
      // instead of a generic toast that looks like a no-op.
      console.error("[ConfigurationWizard] save failed", error);
      const detail =
        error?.errorCode && error?.errorCode !== "UNKNOWN"
          ? `${error.errorCode}: ${error.message}`
          : error?.message || "An unexpected error occurred";
      toast({
        title: editMode ? "Failed to update configuration" : "Failed to create configuration",
        description: detail,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!policyName.trim()) {
      toast({ title: "Name required", description: "Enter a policy name to save as draft.", variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      const draftPayloads: CreatePayloadInput[] = selectedPayloads.map((p) => ({
        kind: p.kind!,
        identifier: p.identifier!,
        config: payloadConfigs[`${p.kind}:${p.identifier}`] || {},
      }));
      await policiesApi.createPolicy({
        name: policyName,
        description: policyDescription || undefined,
        payloads: draftPayloads,
        isDraft: true,
      });
      toast({ title: "Draft saved", description: `"${policyName}" saved as draft.` });
      // See note in handleSubmit: setIsDirty(false) alone is enough to
      // re-clean the guard; calling markClean would freeze a stale baseline.
      skipGuardRef.current = true;
      setIsDirty(false);
      navigate("/configurations");
    } catch (error: any) {
      toast({ title: "Failed to save draft", description: error.message || "An unexpected error occurred", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentStepId = STEPS[step]?.id || "";
  const completedStepIds = STEPS.slice(0, step).map((s) => s.id);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b bg-background">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-1" />Back
          </Button>
          <h1 className="text-xl font-semibold">
            {editMode ? "Edit Configuration" : "New Configuration"}
          </h1>
          {isDirty && (
            <Badge
              variant="outline"
              className="gap-1 border-warning/40 bg-warning-muted text-warning-foreground"
              aria-live="polite"
              data-testid="wizard-dirty-indicator"
              title="You have unsaved changes. Leaving this page will prompt for confirmation."
            >
              <CircleDot className="h-3 w-3" aria-hidden="true" />
              Unsaved changes
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {step > 0 && (
            <Button variant="outline" onClick={() => setStep(step - 1)}>Previous</Button>
          )}
          {step === templateStep ? null : step < totalSteps - 1 ? (
            <Button onClick={handleNext} disabled={!canProceed()}>Continue</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleSaveDraft} disabled={isSubmitting}>
                Save as Draft
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting || !canProceed()}>
                {isSubmitting ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {editMode ? "Saving…" : "Creating…"}</>
                ) : (
                  <><Check className="h-4 w-4 mr-1" /> {editMode ? "Save Configuration" : "Deploy"}</>
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      <WizardStepIndicator
        steps={STEPS}
        currentStep={currentStepId}
        completedSteps={completedStepIds}
      />

      {/* Step content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {/* Template Selection (create mode only) */}
          {step === templateStep && !editMode && (
            <div>
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold">Choose a Template</h2>
                <p className="text-muted-foreground mt-1">Select a starting point for your configuration profile</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                {configTemplates.map((template) => (
                  <GlassCard
                    key={template.id}
                    variant="default"
                    hover={false}
                    className={cn(
                      "cursor-pointer transition-all",
                      selectedTemplateId === template.id && "ring-2 ring-primary/30 shadow-xl"
                    )}
                    onClick={() => setSelectedTemplateId(template.id)}
                  >
                    <GlassCardHeader className="pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="mb-3 flex items-center justify-between">
                            {template.icon}
                            {template.id !== "custom-profile" && templatePayloadCounts[template.id] > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {templatePayloadCounts[template.id]} payload{templatePayloadCounts[template.id] !== 1 ? "s" : ""}
                              </Badge>
                            )}
                          </div>
                          <GlassCardTitle className="text-lg">{template.name}</GlassCardTitle>
                          <GlassCardDescription className="text-sm mt-2">
                            {template.description}
                          </GlassCardDescription>
                        </div>
                      </div>
                    </GlassCardHeader>
                    <GlassCardContent className="pt-0">
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Platforms</p>
                          <div className="flex flex-wrap gap-1">
                            {template.platforms.map((platform) => (
                              <span
                                key={platform}
                                className="px-2 py-1 bg-secondary text-secondary-foreground rounded text-xs"
                              >
                                {platform}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="pt-2 border-t">
                          <Button
                            variant={selectedTemplateId === template.id ? "default" : "outline"}
                            size="sm"
                            className="w-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTemplateSelect(template);
                            }}
                          >
                            {selectedTemplateId === template.id ? (
                              <>Continue <ArrowRight className="h-4 w-4 ml-1" /></>
                            ) : (
                              "Select"
                            )}
                          </Button>
                        </div>
                      </div>
                    </GlassCardContent>
                  </GlassCard>
                ))}
              </div>
            </div>
          )}

          {/* Scope & Assign (final step) — rendered below after configure */}

          {/* Policy Info */}
          {step === infoStep && (
            <div className="text-center">
              <h2 className="text-xl font-semibold">Policy Information</h2>
              <p className="text-muted-foreground mt-1">Enter a name and description for this configuration profile</p>
              <div className="max-w-lg mx-auto mt-6 space-y-4 text-left">
                <div className="space-y-1.5">
                  <Label htmlFor="policyName">Policy Name <span className="text-destructive">*</span></Label>
                  <Input
                    id="policyName"
                    placeholder="e.g. Corporate Wi-Fi + VPN"
                    value={policyName}
                    onChange={(e) => { setPolicyName(e.target.value); markDirty(); }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="policyDesc">Description</Label>
                  <Textarea
                    id="policyDesc"
                    placeholder="Optional description of what this configuration does"
                    value={policyDescription}
                    onChange={(e) => { setPolicyDescription(e.target.value); markDirty(); }}
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Select Payloads */}
          {step === payloadsStep && (
            <PayloadSelector selected={selectedPayloads} onSelectionChange={(v) => { setSelectedPayloads(v); markDirty(); }} />
          )}

          {/* Configure Payloads */}
          {step === configureStep && (
            <div className="space-y-4">
              {selectedPayloads.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No payloads selected. Go back to select payloads.</p>
              ) : selectedPayloads.length === 1 ? (
                <PayloadConfigPanel
                  payload={selectedPayloads[0]}
                  config={payloadConfigs[`${selectedPayloads[0].kind}:${selectedPayloads[0].identifier}`] || {}}
                  onConfigChange={(config) =>
                    handleConfigChange(selectedPayloads[0].kind!, selectedPayloads[0].identifier!, config)
                  }
                />
              ) : (
                <Tabs defaultValue={`${selectedPayloads[0].kind}:${selectedPayloads[0].identifier}`}>
                  <TabsList className="flex-wrap h-auto gap-1">
                    {selectedPayloads.map((p) => (
                      <TabsTrigger key={`${p.kind}:${p.identifier}`} value={`${p.kind}:${p.identifier}`} className="text-xs">
                        {p.title || p.identifier}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {selectedPayloads.map((p) => (
                    <TabsContent key={`${p.kind}:${p.identifier}`} value={`${p.kind}:${p.identifier}`}>
                      <PayloadConfigPanel
                        payload={p}
                        config={payloadConfigs[`${p.kind}:${p.identifier}`] || {}}
                        onConfigChange={(config) => handleConfigChange(p.kind!, p.identifier!, config)}
                      />
                    </TabsContent>
                  ))}
                </Tabs>
              )}
            </div>
          )}

          {/* Scope & Assign */}
          {step === scopeStep && (
            <ScopeAndAssignStep
              selectedCustomers={selectedCustomers}
              onCustomersChange={(v) => { setSelectedCustomers(v); markDirty(); }}
              allCustomers={allCustomers}
              onAllCustomersChange={(v) => { setAllCustomers(v); markDirty(); }}
              selectedTags={selectedTagIds}
              onTagsChange={(v) => { setSelectedTagIds(v); markDirty(); }}
              payloadCount={selectedPayloads.length}
              platformCount={0}
              policyId={editMode ? editId : undefined}
            />
          )}

          {/* Review — final confirmation before save (parity with portal). */}
          {step === reviewStep && (
            <div className="max-w-3xl mx-auto space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold">Review Policy Settings</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Confirm the details below, then save to deploy.
                  </p>
                </div>
                <Badge variant="outline" className="border-success/40 bg-success-muted text-success-foreground gap-1">
                  <Check className="h-3 w-3" /> Ready to save
                </Badge>
              </div>

              <GlassCard variant="default" hover={false} className="p-4 space-y-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Policy</div>
                <div className="text-base font-medium">{policyName || "(unnamed policy)"}</div>
                {policyDescription && (
                  <div className="text-sm text-muted-foreground whitespace-pre-wrap">{policyDescription}</div>
                )}
              </GlassCard>

              <GlassCard variant="default" hover={false} className="p-4 space-y-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Selected payloads ({selectedPayloads.length})
                </div>
                {selectedPayloads.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No payloads selected.</div>
                ) : (
                  <ul className="text-sm space-y-1">
                    {selectedPayloads.map((p) => (
                      <li key={`${p.kind}:${p.identifier}`} className="flex items-center gap-2">
                        <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          {p.kind}
                        </span>
                        <span>{p.title || p.identifier}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </GlassCard>

              <GlassCard variant="default" hover={false} className="p-4 space-y-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Assignment</div>
                {allCustomers ? (
                  <div className="text-sm">All customers (current and future)</div>
                ) : selectedCustomers.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    No assignment configured — policy will be saved without tenant deployment.
                  </div>
                ) : (
                  <div className="text-sm">
                    {selectedCustomers.length} tenant{selectedCustomers.length === 1 ? "" : "s"}
                    {selectedTagIds.length > 0 && (
                      <>, {selectedTagIds.length} tag{selectedTagIds.length === 1 ? "" : "s"}</>
                    )}
                  </div>
                )}
              </GlassCard>
            </div>
          )}
        </div>
      </div>

      {/* Unsaved-changes prompt — opens when guard.requestClose() runs while
          the wizard is dirty (Back from step 0, Cancel, route change, browser
          unload). The guard owns open/close state; we just wire its actions. */}
      <UnsavedChangesDialog
        open={guard.confirmOpen}
        onCancel={guard.cancelDiscard}
        onDiscard={guard.confirmDiscard}
        isSaving={isSubmitting}
        surface="ConfigurationWizard"
      />
    </div>
  );
}
